import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { useLanguage } from '@/lib/i18n/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { Wallet, Eye, EyeOff, ShieldCheck, Lock, Mail } from 'lucide-react';
import { ConnectWalletModal } from '@/components/ConnectWalletModal';
import { AuthSlider } from '@/components/AuthSlider';
import { SafeCryptoLogo } from '@/components/SafeCryptoLogo';
import { web3API } from '@/lib/api';
import { toast } from 'sonner';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [requires2FA, setRequires2FA] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const [walletModalOpen, setWalletModalOpen] = useState(false);
  const { login } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await login(email, password);
      if (result?.requires_2fa) {
        setRequires2FA(true);
        setLoading(false);
        return;
      }
      toast.success(t('login.loginSuccess'));
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || t('login.loginFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password, totpCode);
      toast.success(t('login.loginSuccess'));
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || t('login.loginFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleWalletConnect = async (walletInfo) => {
    try {
      const res = await web3API.walletLogin({
        wallet_address: walletInfo.address,
        wallet_type: walletInfo.wallet_type,
        chain: walletInfo.chain,
        signature: walletInfo.signature,
        challenge: walletInfo.challenge,
      });
      localStorage.setItem('cv_token', res.data.token);
      localStorage.setItem('cv_user', JSON.stringify(res.data.user));
      if (res.data.is_new) {
        toast.success(t('login.walletWelcome').replace('${bonus}', res.data.welcome_bonus));
      } else {
        toast.success(t('login.loginSuccess'));
      }
      setWalletModalOpen(false);
      window.location.href = '/dashboard';
    } catch (err) {
      toast.error(err.response?.data?.detail || t('login.loginFailed'));
    }
  };

  if (requires2FA) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center p-6">
        <Card className="w-full max-w-md bg-[hsl(var(--card))] border-[hsl(var(--border))]">
          <CardHeader className="space-y-2">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-10 h-10 rounded-lg bg-[hsl(var(--primary))]/10 flex items-center justify-center">
                <ShieldCheck className="w-5 h-5 text-[hsl(var(--primary))]" />
              </div>
            </div>
            <CardTitle className="text-2xl font-['Space_Grotesk']">{t('login.twoFaTitle')}</CardTitle>
            <CardDescription className="text-[hsl(var(--muted-foreground))]">{t('login.twoFaDesc')}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handle2FASubmit} className="space-y-4">
              {error && (
                <Alert className="bg-[rgba(217,74,74,0.12)] border-[#D94A4A]/20">
                  <AlertDescription className="text-[#D94A4A]">{error}</AlertDescription>
                </Alert>
              )}
              <div className="space-y-2">
                <Label htmlFor="totp">{t('login.twoFaCode')}</Label>
                <Input
                  id="totp"
                  data-testid="auth-2fa-input"
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value)}
                  placeholder={t('login.twoFaCodePlaceholder')}
                  required
                  autoFocus
                  className="bg-[hsl(var(--background))] border-[hsl(var(--border))] text-center text-lg tracking-widest"
                />
              </div>
              <Button type="submit" data-testid="auth-2fa-submit" disabled={loading} className="w-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--primary))]/90 h-11">
                {loading ? t('common.processing') : t('login.twoFaVerify')}
              </Button>
              <Button type="button" variant="ghost" className="w-full text-[hsl(var(--muted-foreground))]" onClick={() => { setRequires2FA(false); setTotpCode(''); setError(''); }}>
                {t('login.twoFaBack')}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(var(--background))] flex">
      <AuthSlider />

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md space-y-6">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-2.5 mb-2">
            <SafeCryptoLogo size={32} />
            <span className="font-bold text-lg font-['Space_Grotesk']">Safe Crypto</span>
          </div>

          <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
            <CardHeader className="space-y-1 pb-4">
              <CardTitle className="text-2xl font-['Space_Grotesk']">{t('login.title')}</CardTitle>
              <CardDescription className="text-[hsl(var(--muted-foreground))]">
                {t('auth.secureLogin')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <Alert className="bg-[rgba(217,74,74,0.12)] border-[#D94A4A]/20">
                    <AlertDescription className="text-[#D94A4A]">{error}</AlertDescription>
                  </Alert>
                )}

                <div className="space-y-2">
                  <Label htmlFor="email">{t('login.email')}</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(var(--muted-foreground))]" />
                    <Input
                      id="email"
                      type="email"
                      data-testid="auth-email-input"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      required
                      className="bg-[hsl(var(--background))] border-[hsl(var(--border))] pl-10"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">{t('login.password')}</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(var(--muted-foreground))]" />
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      data-testid="auth-password-input"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="bg-[hsl(var(--background))] border-[hsl(var(--border))] pl-10 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  data-testid="auth-submit-button"
                  disabled={loading}
                  className="w-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--primary))]/90 h-11 font-semibold active:scale-[0.98] transition-all"
                >
                  {loading ? t('common.processing') : t('login.submit')}
                </Button>

                <p className="text-center text-sm text-[hsl(var(--muted-foreground))]">
                  {t('login.noAccount')}{' '}
                  <Link to="/register" className="text-[hsl(var(--primary))] hover:underline font-medium">{t('login.registerLink')}</Link>
                </p>

                <div className="relative my-2">
                  <Separator className="bg-[hsl(var(--border))]" />
                  <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-[hsl(var(--card))] px-3 text-xs text-[hsl(var(--muted-foreground))]">
                    {t('login.walletLoginOr')}
                  </span>
                </div>

                <Button
                  type="button"
                  variant="outline"
                  data-testid="auth-wallet-connect-button"
                  onClick={() => setWalletModalOpen(true)}
                  className="w-full border-[hsl(var(--border))] hover:bg-white/5 h-11 gap-2"
                >
                  <Wallet className="w-4 h-4" />
                  {t('login.walletLoginBtn')}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Trust footer */}
          <div className="flex items-center justify-center gap-4 text-[10px] text-[hsl(var(--muted-foreground))]/60">
            <span className="flex items-center gap-1"><Lock className="w-3 h-3" /> 256-bit SSL</span>
            <span className="flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> 2FA {t('auth.protected')}</span>
          </div>
        </div>
      </div>

      <ConnectWalletModal
        open={walletModalOpen}
        onOpenChange={setWalletModalOpen}
        onWalletConnected={handleWalletConnect}
        mode="login"
      />
    </div>
  );
}
